import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi, ApiError, type LoginResponse } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { Logo } from '@/components/brand/Logo';

const schema = z.object({
  email: z.string().email('E-mail inválido').min(1, 'Obrigatório'),
  password: z.string().min(6, 'Mínimo 6 caracteres'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const [serverError, setServerError] = useState<string | null>(null);
  // Quando preenchido, estamos na 2ª etapa (usuário tem MFA ativo).
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  /** Finaliza a sessão com os tokens emitidos (comum às duas etapas). */
  async function finishLogin(tokens: LoginResponse) {
    useAuthStore.getState().setTokens(tokens);
    const me = await authApi.me();
    setSession(me, tokens);
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
    navigate(from ?? '/dashboard', { replace: true });
  }

  async function onSubmit(values: FormValues) {
    setServerError(null);
    try {
      const res = await authApi.login(values.email, values.password);
      if (res.mfaRequired) {
        setMfaToken(res.mfaToken); // avança para a etapa do código
        return;
      }
      await finishLogin(res);
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(
          err.status === 401 ? 'E-mail ou senha incorretos' : err.message ?? 'Erro ao entrar',
        );
      } else {
        setServerError('Erro de conexão com o servidor');
      }
    }
  }

  return (
    <div className="relative min-h-screen grid place-items-center bg-canvas px-4">
      {/* Glow decorativo da marca ao fundo */}
      <div className="rad-glow pointer-events-none absolute inset-0" aria-hidden />

      <div className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo size={56} className="rounded-md shadow-rad-md" />
          <div className="mt-4 text-2xl font-semibold tracking-tight text-fg">
            RAD <span className="text-brand-strong">Finance</span>
          </div>
          <div className="mt-1 text-xs uppercase tracking-[0.18em] text-faint">
            Gestão Financeira · Grupo RAD
          </div>
        </div>

        {mfaToken ? (
          <MfaStep
            mfaToken={mfaToken}
            onVerified={finishLogin}
            onBack={() => {
              setMfaToken(null);
              setServerError(null);
            }}
          />
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="bg-card border rounded-lg p-6 space-y-4 shadow-sm"
            noValidate
          >
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="seu@gruporad.com"
                {...register('email')}
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password')}
                aria-invalid={!!errors.password}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            {serverError && (
              <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md p-3">
                {serverError}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Entrando…' : 'Entrar'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

/** 2ª etapa do login: código do app autenticador ou de recuperação. */
function MfaStep({
  mfaToken,
  onVerified,
  onBack,
}: {
  mfaToken: string;
  onVerified: (tokens: LoginResponse) => Promise<void>;
  onBack: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const tokens = await authApi.loginMfa(mfaToken, code.trim());
      await onVerified(tokens);
    } catch (err) {
      setSubmitting(false);
      if (err instanceof ApiError) {
        setError(
          err.status === 401
            ? 'Código inválido ou sessão expirada. Tente novamente.'
            : err.message ?? 'Erro ao verificar o código',
        );
      } else {
        setError('Erro de conexão com o servidor');
      }
    }
  }

  return (
    <form onSubmit={submit} className="bg-card border rounded-lg p-6 space-y-4 shadow-sm" noValidate>
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-fg">Verificação em duas etapas</h2>
        <p className="text-sm text-subtle">
          Digite o código de 6 dígitos do seu app autenticador, ou um código de recuperação.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="mfa-code">Código</Label>
        <Input
          id="mfa-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md p-3">
          {error}
        </div>
      )}

      <Button type="submit" className="w-full" disabled={submitting || code.trim().length < 6}>
        {submitting ? 'Verificando…' : 'Verificar e entrar'}
      </Button>
      <button
        type="button"
        onClick={onBack}
        className="w-full text-sm text-subtle hover:text-fg transition-colors"
      >
        ← Voltar
      </button>
    </form>
  );
}
